import React from 'react';
import {
  ShieldCheck,
  Scale,
  GitBranch,
  ExternalLink,
  LifeBuoy,
  Globe,
  CheckCircle2,
  Lock,
  Heart,
  Coffee,
} from 'lucide-react';
import { OfficeHostType } from '../services/officeService';
import { APP_VERSION } from '../version';

interface AboutTabProps {
  host: OfficeHostType;
  onOpenPrivacy: () => void;
}

export const AboutTab: React.FC<AboutTabProps> = ({ host, onOpenPrivacy }) => {
  return (
    <div className="help-card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Brand Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderBottom: '1px solid #EDEBE9',
          paddingBottom: '14px',
        }}
      >
        <img
          src="/favicon.svg"
          alt="Gantt for M365 Logo"
          width={36}
          height={36}
          style={{ borderRadius: '6px' }}
        />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: '#201F1E' }}>Gantt for M365</h3>
            <span
              style={{
                fontSize: '11px',
                backgroundColor: '#EFF6FC',
                color: '#0078D4',
                padding: '2px 6px',
                borderRadius: '4px',
                fontWeight: 600,
                letterSpacing: '0.3px',
              }}
              title={`Version ${APP_VERSION} (Semantic Versioning)`}
            >
              v{APP_VERSION}
            </span>
          </div>
          <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#605E5C' }}>
            Lightweight, 100% client-side Gantt charts for PowerPoint, Excel, Word, OneNote &amp; Loop.
          </p>
        </div>
      </div>

      {/* Security & Privacy Highlight */}
      <div
        style={{
          backgroundColor: '#E8F5E9',
          border: '1px solid #C8E6C9',
          borderRadius: '6px',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
        }}
      >
        <Lock size={18} color="#2E7D32" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '12px', color: '#1B5E20' }}>
          <strong>100% Client-Side &amp; Local-First:</strong> Zero project data, task names, dates, or presentation content are ever sent to remote servers. All computation executes exclusively in your local browser memory.
        </div>
      </div>

      {/* Core Links & Resources */}
      <div>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#323130' }}>
          Links &amp; Information
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
          {/* Privacy Notice Modal */}
          <button
            onClick={onOpenPrivacy}
            className="btn btn-secondary"
            style={{ justifyContent: 'flex-start', padding: '8px 12px', height: 'auto' }}
          >
            <ShieldCheck size={16} color="#107C41" />
            <div style={{ textAlign: 'left', marginLeft: '6px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600 }}>Privacy Notice</div>
              <div style={{ fontSize: '10px', color: '#605E5C' }}>Zero data collection policy</div>
            </div>
          </button>

          {/* License Modal */}
          <button
            onClick={onOpenPrivacy}
            className="btn btn-secondary"
            style={{ justifyContent: 'flex-start', padding: '8px 12px', height: 'auto' }}
          >
            <Scale size={16} color="#0078D4" />
            <div style={{ textAlign: 'left', marginLeft: '6px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600 }}>MIT License</div>
              <div style={{ fontSize: '10px', color: '#605E5C' }}>Open-source license terms</div>
            </div>
          </button>

          {/* GitHub Repository */}
          <a
            href="https://github.com/marcel-wiedemeier/gantt-o365"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ justifyContent: 'flex-start', padding: '8px 12px', height: 'auto', textDecoration: 'none' }}
          >
            <GitBranch size={16} color="#201F1E" />
            <div style={{ textAlign: 'left', marginLeft: '6px', flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>GitHub Repository</span>
                <ExternalLink size={11} color="#605E5C" />
              </div>
              <div style={{ fontSize: '10px', color: '#605E5C' }}>Source code &amp; releases</div>
            </div>
          </a>

          {/* Support & Issues */}
          <a
            href="https://github.com/marcel-wiedemeier/gantt-o365/issues"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ justifyContent: 'flex-start', padding: '8px 12px', height: 'auto', textDecoration: 'none' }}
          >
            <LifeBuoy size={16} color="#D83B01" />
            <div style={{ textAlign: 'left', marginLeft: '6px', flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Support &amp; Feedback</span>
                <ExternalLink size={11} color="#605E5C" />
              </div>
              <div style={{ fontSize: '10px', color: '#605E5C' }}>Report bugs or request features</div>
            </div>
          </a>

          {/* Live Web App */}
          <a
            href="https://gantt.wdmr.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ justifyContent: 'flex-start', padding: '8px 12px', height: 'auto', textDecoration: 'none' }}
          >
            <Globe size={16} color="#0078D4" />
            <div style={{ textAlign: 'left', marginLeft: '6px', flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Web App URL</span>
                <ExternalLink size={11} color="#605E5C" />
              </div>
              <div style={{ fontSize: '10px', color: '#605E5C' }}>https://gantt.wdmr.dev</div>
            </div>
          </a>

          {/* Buy Me a Coffee */}
          <a
            href="https://buymeacoffee.com/wdmr"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ justifyContent: 'flex-start', padding: '8px 12px', height: 'auto', textDecoration: 'none' }}
          >
            <Coffee size={16} color="#D83B01" />
            <div style={{ textAlign: 'left', marginLeft: '6px', flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Buy Me a Coffee</span>
                <ExternalLink size={11} color="#605E5C" />
              </div>
              <div style={{ fontSize: '10px', color: '#605E5C' }}>Support project development</div>
            </div>
          </a>
        </div>
      </div>

      {/* Footer Info */}
      <div
        style={{
          borderTop: '1px solid #EDEBE9',
          paddingTop: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '11px',
          color: '#605E5C',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>Created with</span>
          <Heart size={12} color="#D83B01" fill="#D83B01" />
          <span>by Marcel Wiedemeier</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <CheckCircle2 size={12} color="#107C41" />
          <span>Active Host: {host}</span>
        </div>
      </div>
    </div>
  );
};
